import React, { useState, useEffect, useMemo, useRef, Fragment } from 'react';
import autoload from '../../helpers/autoload';
import TaggingSection from './TaggingSection';
import FeaturedImageSlot from './FeaturedImageSlot';
import {
  validateForm,
  clearFieldError,
  clearValidationErrors,
  addValidationErrors,
  scrollToFirstError,
} from './formValidationUtils';
import './ProfileForm.css';

const DynamicProfileForm = ({
  contactId,
  profileData = null,
  isEdit = false,
  apiBaseUrl,
  exhibitorTypes = {},
  companyTypeOptions = [],
  contactFiles: initialContactFiles = [],
  approvedTags = {},
  currentTags = [],
  proposedTags = [],
  pendingChangesData = null,
  isAdmin = false,
  hasApplications = false,
  workflowMode = 'standard',
  workflowData = {},
}) => {
  const formRef = useRef(null);

  const [formData, setFormData] = useState({
    exhibitor_type: 'exhibitor',
    company_name: '',
    title: '',
    sponsor: false,
    website_url: '',
    promo_link: '',
    events_link: '',
    work_description: '',
    bipoc: false,
    bipoc_details: '',
    years_experience: 0,
  });

  const [selectedTags, setSelectedTags] = useState(new Set());
  const [proposedTagsList, setProposedTagsList] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [approvedTagsData, setApprovedTagsData] = useState({});
  const [loadingTags, setLoadingTags] = useState(false);
  const [isAdminUser, setIsAdminUser] = useState(isAdmin);
  const [exhibitorTypesData, setExhibitorTypesData] = useState({});

  const [featuredPictures, setFeaturedPictures] = useState({
    featured_picture_1_id: null,
    featured_picture_2_id: null,
    featured_picture_3_id: null,
    featured_picture_4_id: null,
    featured_picture_5_id: null,
  });

  const [loading, setLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState('');
  const [uploadingImages, setUploadingImages] = useState({});
  const [contactFiles, setContactFiles] = useState(initialContactFiles);
  const [formErrors, setFormErrors] = useState([]);

  // Helper function to get the effective data (pending changes override current data)
  const getEffectiveData = () => {
    if (pendingChangesData && isEdit) {
      // Merge current profile data with pending changes, excluding metadata
      const effectiveData = { ...profileData };

      // Apply all pending changes except metadata keys (starting with _)
      Object.entries(pendingChangesData).forEach(([key, value]) => {
        if (!key.startsWith('_')) {
          effectiveData[key] = value;
        }
      });

      return effectiveData;
    }
    return profileData;
  };

  // Helper function to get effective tags
  const getEffectiveTags = () => {
    console.log('getEffectiveTags called');
    console.log('pendingChangesData:', pendingChangesData);
    console.log('currentTags:', currentTags);

    if (pendingChangesData && pendingChangesData._tag_metadata && isEdit) {
      const effectiveTags = pendingChangesData._tag_metadata.tag_ids || [];
      console.log('Using pending tag_ids:', effectiveTags);
      return effectiveTags;
    }
    console.log('Using currentTags:', currentTags);
    return currentTags;
  };

  // Helper function to get effective proposed tags
  const getEffectiveProposedTags = () => {
    console.log('getEffectiveProposedTags called');
    console.log('pendingChangesData:', pendingChangesData);
    console.log('proposedTags:', proposedTags);

    if (pendingChangesData && pendingChangesData._tag_metadata && isEdit) {
      const effectiveProposed =
        pendingChangesData._tag_metadata.proposed_tags || [];
      console.log('Using pending proposed_tags:', effectiveProposed);
      return effectiveProposed;
    }
    console.log('Using proposedTags:', proposedTags);
    return proposedTags;
  };

  useEffect(() => {
    if (Object.keys(approvedTags).length > 0) {
      setApprovedTagsData(approvedTags);

      if (allCategories.length === 0) {
        setAllCategories(Object.keys(approvedTags).sort());
      }
    }

    if (Object.keys(exhibitorTypes).length > 0) {
      setExhibitorTypesData(exhibitorTypes);
    }

    setIsAdminUser(isAdmin);

    if (contactId && Object.keys(approvedTags).length === 0) {
      fetchProfileData();
    }
  }, []);

  // Separate useEffect to handle tags when pendingChangesData or currentTags change
  useEffect(() => {
    // Use effective tags (pending if available, otherwise current)
    const effectiveTags = getEffectiveTags();
    console.log('Setting effective tags:', effectiveTags);
    if (effectiveTags.length > 0) {
      setSelectedTags(new Set(effectiveTags));
    }
  }, [pendingChangesData, currentTags]);

  // Separate useEffect for proposed tags to prevent them from being overwritten
  useEffect(() => {
    // Use effective proposed tags
    const effectiveProposedTags = getEffectiveProposedTags();
    console.log('Setting effective proposed tags:', effectiveProposedTags);
    if (effectiveProposedTags.length > 0) {
      setProposedTagsList(effectiveProposedTags);
    }
  }, [pendingChangesData, proposedTags]);

  // Scroll to top when errors are displayed in exhibitor application workflow
  useEffect(() => {
    if (workflowMode === 'exhibitor_application' && formErrors.length > 0) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [formErrors, workflowMode]);

  useEffect(() => {
    if (isEdit && profileData) {
      // Use effective data (with pending changes if available)
      const effectiveData = getEffectiveData();

      if (effectiveData) {
        setFormData(prev => ({ ...prev, ...effectiveData }));

        // Handle featured pictures with pending changes
        const pictures = {};
        for (let i = 1; i <= 5; i++) {
          const key = `featured_picture_${i}_id`;
          if (effectiveData[key]) {
            pictures[key] = effectiveData[key];
          }
        }
        setFeaturedPictures(prev => ({ ...prev, ...pictures }));
      }
    } else if (!isEdit && profileData && Object.keys(profileData).length > 0) {
      // For new profiles, use any provided profile data
      setFormData(prev => ({ ...prev, ...profileData }));
     const pictures = Object.fromEntries(
       Array.from({ length: 5 }, (_, i) => `featured_picture_${i + 1}_id`)
         .filter(key => profileData[key])
         .map(key => [key, profileData[key]]),
     );
      setFeaturedPictures(prev => ({ ...prev, ...pictures }))
    }
  }, [profileData, pendingChangesData, isEdit]);

  const fetchProfileData = async () => {
    setLoadingTags(true);
    try {
      const response = await fetch(`${apiBaseUrl}/profile_data`);
      if (response.ok) {
        const data = await response.json();
        console.log('fetchProfileData response:', data);

        if (data.approved_tags) {
          setApprovedTagsData(data.approved_tags);
        }

        if (data.all_categories) {
          setAllCategories(data.all_categories);
        }

        // Only set tags if we don't have pending changes data
        if (data.current_tags.length !== 0 && !pendingChangesData) {
          console.log('Setting current_tags from fetchProfileData');
          setSelectedTags(new Set(data.current_tags));
        }
        if (data.proposed_tags.length !== 0 && !pendingChangesData) {
          console.log('Setting proposed_tags from fetchProfileData');
          setProposedTagsList(data.proposed_tags);
        }
        if (data.contact_files) {
          setContactFiles(data.contact_files);
        }
        if (data.exhibitor_types) {
          setExhibitorTypesData(data.exhibitor_types);
        }
        if (data.is_admin !== undefined) {
          setIsAdminUser(data.is_admin);
        }
      }
    } catch (error) {
      console.error('Error fetching profile data:', error);
    } finally {
      setLoadingTags(false);
    }
  };

  const handleInputChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleInputBlur = e => {
    const { name } = e.target;
    clearFieldError(formRef, name);
  };

  const allAvailableTags = useMemo(() => {
    const tags = [];
    const sortedCategories = Object.entries(approvedTagsData).sort(([a], [b]) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' }),
    );

    sortedCategories.forEach(([category, categoryTags]) => {
      categoryTags.forEach(tag => {
        tags.push({ ...tag, category });
      });
    });
    return tags;
  }, [approvedTagsData]);

  const handleTagToggle = tagId => {
    setSelectedTags(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tagId)) {
        newSet.delete(tagId);
      } else {
        newSet.add(tagId);
      }
      return newSet;
    });
  };

  const handleAddProposedTag = (tagName, category, description, onClose) => {
    if (!tagName) return;

    const newProposedTag = {
      name: tagName,
      category: category,
      description: description,
      id: `temp_${Date.now()}`,
    };

    setProposedTagsList(prev => [...prev, newProposedTag]);

    if (onClose && typeof onClose === 'function') {
      onClose();
    }
  };

  const handleImageUpload = async (file, slotNumber) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      console.error('Please select an image file.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      console.error('Image must be smaller than 5MB.');
      return;
    }

    if (file.size < 25 * 1024) {
      console.error('Image must be larger than 25KB.');
      return;
    }

    setUploadingImages(prev => ({ ...prev, [slotNumber]: true }));

    try {
      const formData = new FormData();
      formData.append('contact_file[upload]', file);

      const response = await fetch(`${apiBaseUrl}/upload_contact_file`, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content || '',
        },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setContactFiles(prev => [
          ...prev,
          {
            id: data.id,
            filename: data.filename,
            url: data.url,
          },
        ]);

        setFeaturedPictures(prev => ({
          ...prev,
          [`featured_picture_${slotNumber}_id`]: data.id,
        }));
      } else {
        const errorData = await response.json();
        console.error(
          `Upload failed: ${
            errorData.errors ? errorData.errors.join(', ') : 'Unknown error'
          }`,
        );
      }
    } catch (error) {
      console.error(`Upload error: ${error.message}`);
    } finally {
      setUploadingImages(prev => ({ ...prev, [slotNumber]: false }));
    }
  };

  const handleImageSelect = (imageId, slotNumber) => {
    setFeaturedPictures(prev => ({
      ...prev,
      [`featured_picture_${slotNumber}_id`]: imageId,
    }));
  };

  const handleImageRemove = slotNumber => {
    setFeaturedPictures(prev => ({
      ...prev,
      [`featured_picture_${slotNumber}_id`]: null,
    }));
  };

  const getImageUrl = imageId => {
    if (!imageId) return null;
    const contactFile = contactFiles.find(
      file => file.id === parseInt(imageId),
    );
    return contactFile ? contactFile.url : null;
  };

  const handleSubmitAsPending = async e => {
    e.preventDefault();
    setLoadingAction('pending');
    await handleSubmitInternal(true);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoadingAction('save');
    await handleSubmitInternal(false);
  };

  const handleSubmitInternal = async saveAsPending => {
    clearValidationErrors(formRef);

    const { isValid, errors } = validateForm(formData, isEdit, workflowMode);

    if (!isValid) {
      addValidationErrors(formRef, errors);
      setLoading(false);
      setLoadingAction('');

      setTimeout(() => {
        scrollToFirstError();
      }, 100);

      return;
    }

    setLoading(true);

    try {
      let url, method;

      if (workflowMode === 'exhibitor_application') {
        // Use the exhibitor application workflow URL for both create and update
        url = workflowData.submitUrl;
        method = 'PATCH';
      } else {
        // Use the standard profile API
        url = isEdit
          ? `${apiBaseUrl}/update_profile`
          : `${apiBaseUrl}/create_profile`;
        method = isEdit ? 'PATCH' : 'POST';
      }

      const ensureProtocol = urlString => {
        if (!urlString || urlString.trim() === '') return urlString;
        const trimmed = urlString.trim();
        if (!/^https?:\/\//i.test(trimmed)) {
          return `https://${trimmed}`;
        }
        return trimmed;
      };

      const processedFormData = {
        ...formData,
        website_url: ensureProtocol(formData.website_url),
        events_link: ensureProtocol(formData.events_link),
        promo_link: ensureProtocol(formData.promo_link),
      };

      const processedProposedTags = proposedTagsList.map(tag => {
        const processedTag = {
          name: tag.name,
          category: tag.category,
          description: tag.description || '',
        };

        // If this is an existing proposed tag (not a temp one), include its ID
        // so the backend knows it's an update to an existing proposed tag
        if (tag.id && !tag.id.toString().startsWith('temp_')) {
          processedTag.id = tag.id;
        }

        return processedTag;
      });

      let payload;

      if (workflowMode === 'exhibitor_application') {
        // For exhibitor application workflow, use the nested attributes format
        payload = {
          profile: {
            exhibitor_profile_attributes: {
              ...processedFormData,
              tag_ids: Array.from(selectedTags),
              proposed_tags: processedProposedTags,
            }
          },
          featured_picture: featuredPictures,
        };
      } else {
        // Standard profile API format
        payload = {
          exhibitor_profile: {
            ...processedFormData,
            tag_ids: Array.from(selectedTags),
            proposed_tags: processedProposedTags,
          },
          featured_picture: featuredPictures,
          save_as_pending: saveAsPending.toString(),
        };
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content || '',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.success) {
        if (workflowMode === 'exhibitor_application') {
          // For exhibitor application workflow, redirect to the URL provided in the response
          window.location.href = data.redirect_url;
        } else {
          // Standard profile redirect
          window.location.href = `/profile/${contactId}`;
        }
      } else {
        setLoading(false);
        setLoadingAction('');

        // Handle validation errors
        if (data.errors) {
          if (Array.isArray(data.errors)) {
            if (workflowMode === 'exhibitor_application') {
              // For exhibitor application workflow, display errors at the top
              setFormErrors(data.errors);
            } else {
              // For standard workflow, add validation errors to the form fields
              addValidationErrors(formRef, data.errors);
              setTimeout(() => {
                scrollToFirstError();
              }, 100);
            }
          } else {
            console.error(`Error: ${data.errors.join(', ')}`);
          }
        } else {
          console.error('Unknown error occurred');
        }
      }
    } catch (error) {
      setLoading(false);
      setLoadingAction('');
      console.error(`Error: ${error.message}`);
    }
  };

  const getExhibitorTypeLabel = key => {
    return exhibitorTypesData[key] || key;
  };

  const getFieldsForType = type => {
    switch (type) {
      case 'artist':
        return {
          workTypeOptions: companyTypeOptions,
          workTypeLabel: 'Type of Artwork',
          descriptionLabel: 'Description of Artwork',
        };
      case 'author':
        return {
          workTypeOptions: companyTypeOptions,
          workTypeLabel: 'Type of Writing',
          descriptionLabel: 'Description of Writing',
        };
      default:
        return {
          workTypeOptions: companyTypeOptions,
          workTypeLabel: 'Type of Products / Services',
          descriptionLabel: 'Description',
        };
    }
  };

  const currentFields = getFieldsForType(formData.exhibitor_type);

  const renderFormActions = () => {
    if (loading) {
      return (
        <div className="attr">
          <div className="name-narrow">&nbsp;</div>
          <div className="value-full">
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <i
                className="fas fa-spinner fa-spin"
                style={{ fontSize: '24px', marginBottom: '10px' }}
              ></i>
              <div style={{ fontSize: '16px', color: '#666' }}>
                {workflowMode === 'exhibitor_application'
                  ? isEdit ? 'Saving profile...' : 'Creating profile...'
                  : loadingAction === 'pending'
                  ? isEdit
                    ? 'Saving as pending...'
                    : 'Creating as pending...'
                  : isEdit
                  ? 'Saving and approving...'
                  : 'Creating and approving...'}
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (workflowMode === 'exhibitor_application') {
      return (
        <div className="attr">
          <div className="name">&nbsp;</div>
          <div className="value-full">
            <a
              href={workflowData.backUrl}
              className="button"
              style={{ marginRight: '6px' }}
            >
              Go Back
            </a>
            <button type="submit" className="button">
              Submit
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="attr">
        <div className="name">&nbsp;</div>
        <div className="value-full">
          <button
            type="button"
            className="button"
            onClick={() => window.history.back()}
            style={{ marginRight: '6px' }}
          >
            Cancel
          </button>

          {isAdminUser ? (
            <>
              <button
                type="button"
                className="button admin-only-button"
                onClick={handleSubmitAsPending}
                style={{ marginRight: '6px' }}
              >
                Pending
              </button>

              <button type="submit" className="button admin-only-button">
                Approved
              </button>
            </>
          ) : (
            <button type="submit" className="button">
              {`${workflowMode === 'exhibitor_application' ? 'Submit' : 'Save'}`}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <Fragment>
      {workflowMode !== 'exhibitor_application' && (
        <div className="page-title">
          {`${isEdit ? 'Edit' : 'Create'} ${getExhibitorTypeLabel(formData.exhibitor_type)} Profile Information`}
        </div>
      )}

      {workflowMode === 'exhibitor_application' && (
        <div className="attributes">
          <div className="attr">
            <div className="name">Exhibitor:</div>
            <div className="value">{workflowData.exhibitorName}</div>
          </div>
          <div className="attr">
            <div className="name">Convention:</div>
            <div className="value">{workflowData.conventionName}</div>
          </div>
        </div>
      )}

      <div className="columns">
        <div className="panel">
          <form ref={formRef} onSubmit={handleSubmit}>
            {/* Error Messages - Only show in exhibitor application workflow */}
            {workflowMode === 'exhibitor_application' && formErrors.length > 0 && (
              <div id="error_explanation">
                <ul className="error-messages">
                  {formErrors.map((error, index) => (
                    <li key={index} className="red">{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="attributes">
              {/* Profile Type - Admin Only (not shown in exhibitor application workflow) */}
              {isAdminUser && workflowMode !== 'exhibitor_application' && (
                <div className="attr">
                  <div className="name admin-only">Profile Type:</div>
                  <div className="value-full">
                    <select
                      name="exhibitor_type"
                      value={formData.exhibitor_type}
                      onChange={handleInputChange}
                      onBlur={handleInputBlur}
                      disabled={
                        hasApplications &&
                        formData.exhibitor_type === 'exhibitor'
                      }
                    >
                      {Object.entries(exhibitorTypesData).map(
                        ([key, label]) => (
                          <option key={key} value={key}>
                            {label}
                          </option>
                        ),
                      )}
                    </select>
                    {hasApplications &&
                      formData.exhibitor_type === 'exhibitor' && (
                        <div
                          className="form_comment"
                          style={{ color: '#d9534f', marginTop: '5px' }}
                        >
                          Cannot change profile type from "Exhibitor" because
                          this contact has existing exhibitor applications.
                        </div>
                      )}
                  </div>
                </div>
              )}

              {/* Sponsor - Admin Only (not shown in exhibitor application workflow) */}
              {isAdminUser && workflowMode !== 'exhibitor_application' && (
                <div className="attr">
                  <div className="name admin-only">Sponsor:</div>
                  <div className="value">
                    <input
                      type="checkbox"
                      name="sponsor"
                      checked={formData.sponsor}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              )}

              <div className="attr">
                <div className="name">Exhibiting Name:</div>
                <div className="value-full">
                  <input
                    type="text"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                  />
                </div>
              </div>

              {/* Title - Admin Only (not shown in exhibitor application workflow) */}
              {isAdminUser && workflowMode !== 'exhibitor_application' && (
                <div className="attr">
                  <div className="name admin-only">Title:</div>
                  <div className="value-full">
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      onBlur={handleInputBlur}
                      placeholder="Level/title of sponsorship (e.g., Official Gen Con Pizza)"
                      className="styled-placeholder"
                    />
                  </div>
                </div>
              )}

              <div className="attr">
                <div className="name">Type of Products / Services:</div>
                <div className="value-full">
                  <TaggingSection
                    loadingTags={loadingTags}
                    allAvailableTags={approvedTagsData}
                    allCategories={allCategories}
                    selectedTags={selectedTags}
                    handleTagToggle={handleTagToggle}
                    proposedTagsList={proposedTagsList}
                    setProposedTagsList={setProposedTagsList}
                    handleAddProposedTag={handleAddProposedTag}
                    pendingChangesData={pendingChangesData}
                  />
                </div>
              </div>

              <div className="attr">
                <div className="name">Website:</div>
                <div className="value-full">
                  <input
                    type="text"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    placeholder="https://example.com"
                    className="styled-placeholder"
                  />
                </div>
              </div>

              <div className="attr">
                <div className="name">Events Link:</div>
                <div className="value-full">
                  <input
                    type="text"
                    name="events_link"
                    value={formData.events_link}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    placeholder="https://example.com/events"
                    className="styled-placeholder"
                  />
                </div>
              </div>

              <div className="attr">
                <div className="name">{currentFields.descriptionLabel}:</div>
                <div className="value-full">
                  <textarea
                    name="work_description"
                    value={formData.work_description}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    rows="4"
                    maxLength="600"
                    style={{ width: '100%' }}
                  />
                </div>
              </div>

              {/* BIPOC Organization - Available to all users in exhibitor application workflow */}
              {(isAdminUser || workflowMode === 'exhibitor_application') && (
                <div className="attr">
                  <div className="name">BIPOC Organization:</div>
                  <div className="value-full">
                    <input
                      type="checkbox"
                      name="bipoc"
                      checked={formData.bipoc}
                      onChange={handleInputChange}
                    />
                    <div className="form_comment">
                      Check if your organization is BIPOC owned or led.
                    </div>
                  </div>
                </div>
              )}

              {/* BIPOC Details - Show only if BIPOC is checked */}
              {(isAdminUser || workflowMode === 'exhibitor_application') && formData.bipoc && (
                <div className="attr bipoc_details">
                  <div className="name">BIPOC Details:</div>
                  <div className="value">
                    <textarea
                      name="bipoc_details"
                      value={formData.bipoc_details}
                      onChange={handleInputChange}
                      rows="6"
                      maxLength="5000"
                      style={{ width: '100%' }}
                    />
                    <div className="form_comment">
                      Please indicate how your organization identifies as BIPOC,
                      along with any details on your representation of the BIPOC
                      community.
                    </div>
                  </div>
                </div>
              )}

              {/* Years Experience - Admin Only (not shown in exhibitor application workflow) */}
              {isAdminUser && workflowMode !== 'exhibitor_application' && (
                <div className="attr">
                  <div className="name">
                    Number of Years Exhibited:
                  </div>
                  <div className="value">
                    <input
                      type="text"
                      name="years_experience"
                      value={formData.years_experience}
                      onChange={handleInputChange}
                      onBlur={handleInputBlur}
                    />
                  </div>
                </div>
              )}

              <div className="attr">
                <div className="name">Featured Images:</div>
                <div className="value-full">
                  <div className="featured-images-container">
                    {[1, 2, 3, 4, 5].map(slotNumber => (
                      <FeaturedImageSlot
                        key={slotNumber}
                        slotNumber={slotNumber}
                        currentImageId={
                          featuredPictures[`featured_picture_${slotNumber}_id`]
                        }
                        isUploading={uploadingImages[slotNumber]}
                        contactFiles={contactFiles}
                        onImageUpload={handleImageUpload}
                        onImageSelect={handleImageSelect}
                        onImageRemove={handleImageRemove}
                        getImageUrl={getImageUrl}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {renderFormActions()}
            </div>
          </form>
        </div>
      </div>
    </Fragment>
  );
};

export default autoload(DynamicProfileForm, 'dynamic-profile-form');
